'use client'

import { clsx } from 'clsx'
import {
  type FC,
  type SubmitEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import {
  RxByPrincipleReasonSet,
  RxByPrincipleSelectableReason,
} from '@redteclab/api/clients/bully'

import {
  Button,
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTitle,
  Notification,
  Radio,
} from 'base-ui'

import { ProductPageRxByPrincipleHeader } from './ProductPageRxByPrincipleHeader'

const SELECTABLE_REASON_INPUT_NAME = 'reason'

const selectableReasonsByReasonSet: Record<
  RxByPrincipleReasonSet,
  RxByPrincipleSelectableReason[]
> = {
  [RxByPrincipleReasonSet.Dextromethorfan]: [
    RxByPrincipleSelectableReason.DryCough,
  ],
  [RxByPrincipleReasonSet.Paracetamol]: [
    RxByPrincipleSelectableReason.Fever,
    RxByPrincipleSelectableReason.Pain,
    RxByPrincipleSelectableReason.Arthritis,
  ],
  [RxByPrincipleReasonSet.ParacetamolIbuprofen]: [
    RxByPrincipleSelectableReason.ShortTermTreatmentMildPain,
  ],
  [RxByPrincipleReasonSet.ParacetamolPseudoEfedrine]: [
    RxByPrincipleSelectableReason.NasalBlockage,
    RxByPrincipleSelectableReason.FeverAndHeadache,
  ],
  [RxByPrincipleReasonSet.PseudoEfedrine]: [
    RxByPrincipleSelectableReason.NasalBlockage,
  ],
}

type ProductRxByPrincipleDialogProps = DialogProps & {
  autoOpenOnLocationHash?: boolean
  onOpen: () => void
  onReasonSelect: (reason: RxByPrincipleSelectableReason) => void
  productName: string
  reasonSet: RxByPrincipleReasonSet
}

const getReasonLabel = (
  reason: RxByPrincipleSelectableReason,
  intl: IntlShape,
): string => {
  const reasonLabel = {
    [RxByPrincipleSelectableReason.Arthritis]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.arthritis',
      }),
    [RxByPrincipleSelectableReason.DryCough]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.dryCough',
      }),
    [RxByPrincipleSelectableReason.Fever]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.fever',
      }),
    [RxByPrincipleSelectableReason.FeverAndHeadache]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.feverAndHeadache',
      }),
    [RxByPrincipleSelectableReason.NasalBlockage]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.nasalBlockage',
      }),
    [RxByPrincipleSelectableReason.Pain]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.pain',
      }),
    [RxByPrincipleSelectableReason.ShortTermTreatmentMildPain]: (): string =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.reason.shortTermTreatmentMildPain',
      }),
  }

  return reasonLabel[reason]()
}

const getConfirmationDanger = (
  reasonSet: RxByPrincipleReasonSet,
  intl: IntlShape,
): string => {
  const confirmationDanger: Record<RxByPrincipleReasonSet, () => string> = {
    [RxByPrincipleReasonSet.Dextromethorfan]: () =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.confirmation.danger.dextromethorfan',
      }),
    [RxByPrincipleReasonSet.Paracetamol]: () =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.confirmation.danger.paracetamol',
      }),
    [RxByPrincipleReasonSet.ParacetamolIbuprofen]: () =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.confirmation.danger.paracetamol',
      }),
    [RxByPrincipleReasonSet.ParacetamolPseudoEfedrine]: () =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.confirmation.danger.paracetamol',
      }),
    [RxByPrincipleReasonSet.PseudoEfedrine]: () =>
      intl.formatMessage({
        id: 'rxByPrincipleModal.confirmation.danger.pseudoEfedrine',
      }),
  }

  return confirmationDanger[reasonSet]()
}

const collectSelectedReasonFromForm = (
  formElement: HTMLFormElement,
): RxByPrincipleSelectableReason | undefined => {
  const reasonInputValue = new FormData(formElement).get(
    SELECTABLE_REASON_INPUT_NAME,
  )

  /**
   * this is needed to avoid casting
   */
  return Object.values(RxByPrincipleSelectableReason).find(
    (item) => item === reasonInputValue,
  )
}

const useAutoOpenDialogDependingOnLocationHash = ({
  autoOpenOnLocationHash,
  onOpen,
}: {
  autoOpenOnLocationHash?: boolean
  onOpen: () => void
}): void => {
  useEffect(() => {
    /**
     * Hash value is defined in [Fock](https://github.com/RedTecLab/fock/blob/b513142499864e4a206d5c57dd38904ca285a770/app/views/assets/scripts/rxByPrincipleModal.ts#L7)
     * and used for opening dialog without JavaScript after redirect to product page
     * if `rx by principle` reason was not provided.
     *
     * We have rx by principle modal in buybox and buytogether, we only need to auto open for the buybox one, this is controlled
     * by `autoOpenOnLocationHash` prop
     */
    if (autoOpenOnLocationHash && location.hash === '#rx-by-principle-modal') {
      onOpen()
    }
  }, [autoOpenOnLocationHash, onOpen])
}

export const ProductPageRxByPrincipleDialog: FC<
  ProductRxByPrincipleDialogProps
> = ({
  autoOpenOnLocationHash,
  onOpen,
  onReasonSelect,
  productName,
  reasonSet,
  ...dialogProps
}) => {
  const { onClose } = dialogProps

  const intl = useIntl()

  const [reasonRequiredErrorMessage, setReasonRequiredErrorMessage] =
    useState(false)

  useAutoOpenDialogDependingOnLocationHash({
    autoOpenOnLocationHash,
    onOpen,
  })

  const onSubmit: SubmitEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault()

      const selectedReason = collectSelectedReasonFromForm(event.currentTarget)
      if (!selectedReason) {
        setReasonRequiredErrorMessage(true)

        return
      }

      setReasonRequiredErrorMessage(false)
      onReasonSelect(selectedReason)
    },
    [onReasonSelect],
  )

  return (
    <Dialog id="rx-by-principle-modal" {...dialogProps}>
      <DialogTitle onClose={onClose}>
        <FormattedMessage id="rxByPrincipleModal.title" />
      </DialogTitle>
      <DialogContent>
        <form
          className="flex flex-col gap-6"
          data-qa-id="rx-by-principle-form"
          noValidate
          onSubmit={onSubmit}
        >
          <ProductPageRxByPrincipleHeader reasonSet={reasonSet} />
          <div className="prose whitespace-normal">
            <FormattedMessage
              id="rxByPrincipleModal.info"
              values={{
                productName: (
                  <span className="mt-6 block text-l font-medium italic text-dark-brand">
                    {productName}
                  </span>
                ),
              }}
            />
          </div>
          <fieldset className="-mt-2 flex flex-col gap-4 rounded-m bg-light-brand-medium p-4">
            <span className="text-m font-medium">
              <FormattedMessage id="rxByPrincipleModal.legend" />
            </span>
            <div className="flex flex-col gap-2">
              {selectableReasonsByReasonSet[reasonSet].map((reason) => (
                <label
                  className="flex gap-2.5"
                  htmlFor={`rx-by-principle-reason-${reason}`}
                  key={reason}
                >
                  <Radio
                    className="shrink-0"
                    data-qa-id={`rx-by-principle-reason-${reason}`}
                    id={`rx-by-principle-reason-${reason}`}
                    name={SELECTABLE_REASON_INPUT_NAME}
                    required
                    size="small"
                    value={reason}
                  />
                  <span>{getReasonLabel(reason, intl)}</span>
                </label>
              ))}
            </div>
            <Notification
              className={clsx(!reasonRequiredErrorMessage && 'hidden')}
              severity="error"
            >
              <FormattedMessage id="rxByPrincipleModal.reasonRequiredErrorMessage" />
            </Notification>
          </fieldset>
          <div className="prose text-xs">
            <p>
              <FormattedMessage id="rxByPrincipleModal.confirmation.usage" />
            </p>
            <p>{getConfirmationDanger(reasonSet, intl)}</p>
          </div>
          <Button
            className="tablet:self-center"
            data-qa-id="rx-by-principle-submit"
            type="submit"
          >
            <FormattedMessage id="rxByPrincipleModal.submit" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
